import { useState, useMemo, useCallback, useEffect } from "react";
import { useSignal } from "@preact/signals-react";
import { getStorage } from "store/local";
import { useStore, insertRecord } from "storev2/formStore";
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useTable } from "storev2/tableStore";
import Column from "./Column";
import CardItem from "../../apps/SaleOrderTabs/components/CardItem";
import HeaderColumn from "./HeaderColumn";
import proxy from "api/proxy";
import { getViewFields, genStoreId } from "componentsv2/tools/functions";
import LoadingIcon from "components/Tools/LoadingIcon";
import BoardForm from "componentsv2/form/BoardForm";
import { cn } from "tools/ui";
import Dialog from "componentsv2/modal/Dialog";
import ModalOrder from "apps/SaleOrderTabs/components/ModalOrder";
import { MessageContainer, useMessage } from "componentsv2/experimental/Message";
import { TIMES_ORDER_STATUS } from "./constants";
import { getOrderTimeStatus } from "apps/SaleOrderTabs/functions/times";

function getDateState(dateStr) {
  if (!dateStr) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const date = new Date(dateStr + (dateStr.includes("T") ? "" : "T00:00:00"));

  const diffDays = Math.floor((date - today) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return "overdue";
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "tomorrow";

  const dayOfWeek = today.getDay();
  const daysUntilNextMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;
  if (diffDays < daysUntilNextMonday) return "thisWeek";

  const daysUntilNextNextMonday = daysUntilNextMonday + 7;
  if (diffDays < daysUntilNextNextMonday) return "nextWeek";

  return null;
}

function groupByStatus(
  records,
  states,
  filterSemaphore,
  userFilter,
  statusField,
  dateField,
  hideTimes,
  kanbanDateGrouping,
  orderBy,
) {
  return Object.keys(states).reduce((acc, state) => {
    acc[state] = (records ?? [])
      .filter((r) => {
        let timeLabel;
        if (!hideTimes) {
          timeLabel = getOrderTimeStatus(
            r["order_status_time."]?.commanded ?? r[dateField],
          )?.label;
        }

        const userId = r["write_uid."]?.id ?? r.write_uid;
        const columnUserFilter = userFilter?.[state];
        const dateState = getDateState(r[dateField]);

        return (
          (kanbanDateGrouping ? dateState === state : r[statusField] === state) &&
          (!filterSemaphore || timeLabel === filterSemaphore) &&
          (!columnUserFilter || userId === columnUserFilter)
        );
      })
      .map((r) => {
        const dateState = getDateState(r[dateField]);
        const enriched = {
          ...r,
          ...(kanbanDateGrouping ? { _dateState: dateState } : {}),
        };
        if (hideTimes) return enriched;
        return {
          ...enriched,
          time: getOrderTimeStatus(
            r["order_status_time."]?.commanded ?? r[dateField],
          )?.label,
        };
      })
      .sort((a, b)=> {
        if (orderBy?.length) {
          for (const [field, dir] of orderBy) {
            const av = new Date(a[field]).getTime() || 0;
            const bv = new Date(b[field]).getTime() || 0;
            if (av !== bv) {
              return dir === "DESC" ? bv - av : av - bv;
            }
          }
        }
        return new Date(a[dateField]) - new Date(b[dateField]);
      });

    return acc;
  }, {});
}

export default function BoardKanban({ layout, setView, handleView }) {
  const {
    states,
    model,
    limit,
    kanbanStatusField = "order_status",
    kanbanDateField = "pos_create_date",
    kanbanDateGrouping = false,
    kanbanTitle,
    kanbanHideTimes = false,
    kanbanHideSearch = false,
    kanbanShowOptions = true,
    kanbanUseModal = true,
    kanbanHorizontal = false,
    kanbanCardComponent: CardComponent,
    transitions = [],
  } = layout;
  const TheCard = CardComponent || CardItem;
  const { getDomain } = useTable();

  const storeId = genStoreId(layout.model, null);
  const currentDomain = getDomain(layout, storeId);

  const queryClient = useQueryClient();
  const _fields = getViewFields(layout, "list");

  // ✅ estados locales
  const [activeRecord, setActiveRecord] = useState(null);
  const [overColumn, setOverColumn] = useState(null);
  const [loading, setLoading] = useState(false);
  const recordStoreId = useSignal(null);
  const openModal = useSignal(false);

  const [modalState, setModalState] = useState({
    open: false,
    record: null,
  });

  const [filterSemaphore, setFilterSemaphore] = useState(null);
  const [searchDomain, setSearchDomain] = useState(null);
  const [filterDomain, setFilterDomain] = useState(null);
  const [userFilter, setUserFilter] = useState({});

  const session = getStorage("ctxSession");
  const currentUserId = session?.user;
  const { messages, hide, error: notifyError } = useMessage();

  const getDomainFn = useCallback(() => {
    const baseDomain = currentDomain ?? [];
    const conditions = [];

    if (baseDomain.length > 0) {
      conditions.push(baseDomain);
    }

    if (searchDomain && searchDomain.length > 0) {
      const isWrappedInAnd = searchDomain[0] === "AND";
      const searchPart = isWrappedInAnd ? searchDomain.slice(1) : searchDomain;
      conditions.push(...searchPart);
    }

    if (filterDomain && filterDomain.length > 0) {
      const isWrappedInAnd = filterDomain[0] === "AND";
      const filterPart = isWrappedInAnd ? filterDomain.slice(1) : filterDomain;
      conditions.push(...filterPart);
    }

    if (conditions.length === 0) {
      return baseDomain;
    }

    if (conditions.length === 1) {
      return conditions[0];
    }

    return ["AND", ...conditions];
  }, [currentDomain, searchDomain, filterDomain]);

  const domainKey = useMemo(() => JSON.stringify(getDomainFn()), [getDomainFn]);

  useEffect(() => {
    const unsubscribe = useTable.subscribe(
      (state) => state.domains,
      (domains) => {
        const domainSearch = domains?.get(storeId);
        if (domainSearch?.search) {
          setSearchDomain(domainSearch.search);
        }
      },
    );
    return () => unsubscribe();
  }, [storeId]);

  // ✅ sensores drag
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const queryKey = [storeId, model, domainKey];

  // ✅ query
  const {
    data: records = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const domain = getDomainFn();
      const { data, error } = await proxy.search(
        model,
        domain ?? [],
        _fields ?? [],
        limit,
      );

      if (error) {
        console.error("ERROR QUERY:", error);
        return [];
      }

      return data ?? [];
    },

    refetchInterval: 10000,
    refetchOnWindowFocus: false,
    retry: 3,
    retryDelay: 2000,
    placeholderData: (prev) => prev,
  });

  // ✅ memo (performance)
  const columnsData = useMemo(() => {
    return groupByStatus(
      records,
      states,
      filterSemaphore,
      userFilter,
      kanbanStatusField,
      kanbanDateField,
      kanbanHideTimes,
      kanbanDateGrouping,
      layout.orderBy,
    );
  }, [
    records,
    states,
    filterSemaphore,
    userFilter,
    kanbanStatusField,
    kanbanDateField,
    kanbanHideTimes,
    kanbanDateGrouping,
    layout.orderBy,
  ]);

  // ✅ grid seguro para tailwind
  const columnsCount = Object.keys(states).length || 1;

  const columnsMap = {
    1: "md:grid-cols-1",
    2: "md:grid-cols-2",
    3: "md:grid-cols-3",
    4: "md:grid-cols-4",
    5: "md:grid-cols-5",
  };

  const columnsView = columnsMap[columnsCount] || "md:grid-cols-4";

  // ✅ handlers
  const handleDragStart = useCallback(({ active }) => {
    const data = active?.data?.current;
    if (data?.record) setActiveRecord(data.record);
    setOverColumn(null);
  }, []);

  const handleDragOver = useCallback(({ over }) => {
    if (!over) return setOverColumn(null);

    const overData = over?.data?.current;

    const toColumn =
      overData?.sortable?.containerId ?? overData?.column ?? over?.id ?? null;

    setOverColumn(toColumn);
  }, []);

  const handleDragEnd = useCallback(
    async ({ active, over }) => {
      setActiveRecord(null);
      setOverColumn(null);

      if (!over) return;

      const activeData = active?.data?.current;
      const overData = over?.data?.current;

      const fromColumn = activeData?.column;
      const toColumn =
        overData?.sortable?.containerId ?? overData?.column ?? over.id;

      if (!fromColumn || !toColumn || fromColumn === toColumn) return;

      const record = activeData.record;
      if (!record) return;

      if (transitions.length > 0) {
        const allowed = transitions
          .filter(([from]) => from === fromColumn)
          .map(([, to]) => to);
        if (!allowed.includes(toColumn)) return;
      }

      const previousRecords = queryClient.getQueryData(queryKey);

      // ✅ optimistic update
      queryClient.setQueryData(queryKey, (old = []) =>
        old.map((rec) =>
          rec.id === record.id
            ? { ...rec, [kanbanStatusField]: toColumn }
            : rec,
        ),
      );

      setLoading(true);

      const revert = () => queryClient.setQueryData(queryKey, previousRecords);

      try {
        const methodField = layout.fields?.[toColumn];
        let response;
        if (methodField?.button_method) {
          response = await proxy.buttonMethod({
            model,
            method: methodField.button_method,
            ids: [record.id],
          });
        } else {
          response = await proxy.save({
            model,
            ids: [record.id],
            values: { [kanbanStatusField]: toColumn },
          });
        }

        if (response?.error) {
          revert();
          notifyError(response.error);
          return;
        }

        await queryClient.invalidateQueries({ queryKey });
      } catch (err) {
        console.error("SAVE ERROR", err);
        revert();
        notifyError(err?.message ?? err);
      } finally {
        setLoading(false);
      }
    },
    [queryClient, queryKey, model, transitions, layout, notifyError],
  );

  const handleModal = useCallback(
    async (record) => {
      if (!record?.id) return;
      if (kanbanUseModal) {
        setModalState({ open: true, record });
      } else {
        const recStoreId = await insertRecord(record.id, layout.model, layout);
        recordStoreId.value = recStoreId;
        openModal.value = true;
      }
    },
    [layout, kanbanUseModal],
  );

  const handleAdd = useCallback(async () => {
    const { newRecord } = useStore.getState();
    const recStoreId = await newRecord(layout);
    if (layout.display === "board") {
      handleView("add", recStoreId);
    } else {
      recordStoreId.value = recStoreId;
      openModal.value = true;
    }
  }, [layout, handleView]);

  const handleCloseModal = useCallback(() => {
    openModal.value = false;
    recordStoreId.value = null;
    refetch();
  }, [refetch]);

  const handleModalView = useCallback(
    (action, recStoreIdValue) => {
      if (action === "return") {
        handleCloseModal();
      } else {
        handleView(action, recStoreIdValue);
      }
    },
    [handleView, handleCloseModal],
  );

  return (
    <div className="h-[77vh]">
      <HeaderColumn
        model={model}
        states={states}
        title={kanbanTitle}
        times={TIMES_ORDER_STATUS}
        layout={layout}
        storeId={storeId}
        filterSemaphore={filterSemaphore}
        setFilterSemaphore={setFilterSemaphore}
        getDomain={getDomainFn}
        setSearchDomain={setSearchDomain}
        setFilterDomain={setFilterDomain}
        kanbanHideTimes={kanbanHideTimes}
        kanbanHideSearch={kanbanHideSearch}
        setView={setView}
        handleAdd={layout.table_action?.includes("add") ? handleAdd : null}
      />

      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div
          className={cn(
            kanbanHorizontal
              ? "flex gap-3 px-2 overflow-x-auto overflow-y-hidden h-full flex-nowrap pb-4 max-h-800"
              : "grid gap-3 px-2 overflow-visible h-full grid-cols-1",
            !kanbanHorizontal && columnsView,
          )}
        >
          {Object.keys(states).map((stateKey) => {
            const items = columnsData[stateKey];
            const color = states[stateKey];

            return (
              <div
                key={stateKey}
                className={
                  kanbanHorizontal
                    ? "min-w-[280px] w-72 flex-shrink-0 h-full"
                    : ""
                }
              >
                <Column
                  stateKey={stateKey}
                  title={stateKey}
                  count={items.length}
                  color={color}
                  model={model}
                  isOverProp={overColumn === stateKey}
                  showOptions={kanbanShowOptions}
                  userFilter={userFilter}
                  setUserFilter={setUserFilter}
                  currentUserId={currentUserId}
                >
                  <SortableContext
                    id={stateKey}
                    items={items.map((i) => i.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {items.map((record) => (
                      <TheCard
                        key={record.id}
                        record={record}
                        state={stateKey}
                        color={color}
                        model={model}
                        handleModal={handleModal}
                      />
                    ))}
                  </SortableContext>
                </Column>
              </div>
            );
          })}
        </div>

        <DragOverlay style={{ pointerEvents: "none" }}>
          {activeRecord ? (
            <TheCard
              record={activeRecord}
              state={kanbanDateGrouping ? getDateState(activeRecord[kanbanDateField]) : activeRecord[kanbanStatusField]}
              color={kanbanDateGrouping ? states?.[getDateState(activeRecord[kanbanDateField])] : states?.[activeRecord[kanbanStatusField]]}
              model={model}
              isOverlay
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {(loading || isLoading) && (
        <div className="w-screen h-screen absolute top-0 left-0 bg-neutral-50/80 z-50 flex items-center justify-center">
          <LoadingIcon size="50px" color="fill-neutral-800" />
        </div>
      )}

      {kanbanUseModal && (
        <Dialog
          openModal={{ value: modalState.open }}
          handleClose={() => setModalState({ open: false, record: null })}
          title={`#${modalState.record?.number ?? ""}`}
        >
          {modalState.record && <ModalOrder order={modalState.record} />}
        </Dialog>
      )}

      <Dialog
        openModal={openModal}
        handleClose={handleCloseModal}
        title={layout.model}
        size="w-2/3 max-h-[90vh] 2xl:w-3/4"
        fixedHeight
      >
        {recordStoreId?.value && (
          <BoardForm
            layout={layout}
            storeId={recordStoreId.value}
            handleView={handleModalView}
            onClose={handleCloseModal}
            view="modal"
            refetch={refetch}
          />
        )}
      </Dialog>
      <MessageContainer
        messages={messages}
        onMessageClose={hide}
        position="bottom-center"
      />
    </div>
  );
}
